import React, { FC, memo, useMemo } from "react";
import AnnotationItemMenu from "./AnnotationItemMenu";
import { Rectangle } from "../../../interfaces/textLayer";
import {
  getAccessibleTextColor,
  tintOnWhite,
} from "../../../../common/utilities/color";
import useRemoveAnnotation from "../../../hooks/useRemoveAnnotation";
import useFindAnnotation from "../../../hooks/useFindAnnotation";
import { useAppSelector } from "../../../../app";
import documentSetDisabledSelector from "../../../../documentSet/selectors/documentSetDisabledSelector";
import { shallowEqual } from "react-redux";

type Props = {
  id: string;
  annotationId: string;
  entityId: string;
  index: number;
  coords: Rectangle;
  rotation: number;
  color: string;
  scrollClassName: string;
  annotationIndex: number;
  tokenIndex: number;
  isTempAnnotation: boolean;
};

// `id` is intentionally not destructured: it is unused in the body but read by
// the `areEqual` comparator below, so it must stay on Props.
const AnnotationItemToken: FC<Props> = ({
  annotationId,
  entityId,
  index,
  coords,
  rotation,
  color,
  scrollClassName,
  annotationIndex,
  tokenIndex,
}) => {
  // Move hooks here to prevent AnnotationItemMenu from re-rendering due to hook changes
  const handleRemove = useRemoveAnnotation(annotationId);
  const handleFind = useFindAnnotation(entityId, index);
  const disabled = useAppSelector(documentSetDisabledSelector, shallowEqual);
  const isAnnotating = useAppSelector(
    (state) => state.appReducer.isAnnotating,
    shallowEqual,
  );

  // Generate style internally - avoids creating new object references in parent
  const style = useMemo(() => {
    const accessibleTextColor = getAccessibleTextColor(color);

    return {
      width: `${coords.width}px`,
      height: `${coords.height}px`,
      transform: `rotate(${rotation}deg)`,
      top: coords.top,
      left: coords.left,
      backgroundColor: tintOnWhite(color, 40),
      outline: `2px solid ${tintOnWhite(color, 50)}`,
      color: accessibleTextColor,
      // CSS variables for dynamic highlighting (used by useAnnotationHighlighting hook).
      // CSSProperties has no index signature, so the custom property names are
      // widened to `string` to keep them out of the known-property check.
      ["--annotation-color" as string]: color,
      ["--annotation-text-color" as string]: accessibleTextColor,
    };
  }, [coords.width, coords.height, coords.top, coords.left, rotation, color]);

  // Memoize menu style (Phase 3 optimization)
  const menuStyle = useMemo(
    () => ({
      top: coords.top - 33,
      left: coords.left + coords.width - 5,
    }),
    [coords.top, coords.left, coords.width],
  );

  return (
    <div className="annotation-layer__item-container-inner">
      <mark
        className={`annotation-layer__item ${scrollClassName}`.trim()}
        style={style}
        id={`${annotationId}-${annotationIndex}-${tokenIndex}`}
      />
      <AnnotationItemMenu
        onRemove={handleRemove}
        onFind={handleFind}
        disabled={disabled}
        isAnnotating={isAnnotating}
        style={menuStyle}
      />
    </div>
  );
};

// Custom comparison to prevent unnecessary re-renders
// Compare actual data instead of object references
const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.scrollClassName === nextProps.scrollClassName &&
    prevProps.coords.top === nextProps.coords.top &&
    prevProps.coords.left === nextProps.coords.left &&
    prevProps.coords.width === nextProps.coords.width &&
    prevProps.coords.height === nextProps.coords.height &&
    prevProps.rotation === nextProps.rotation &&
    prevProps.color === nextProps.color
  );
};

export default memo(AnnotationItemToken, areEqual);
